import { useCallback } from 'react';
import { useTournamentOptInMutation } from '~/client/features/Tournaments/graphql/TournamentOptInMutation.generated';
import useRedirect from '~/client/hooks/router/useRedirect';
import useRouter from '~/client/hooks/router/useRouter';
import useCurrentUser from '~/client/hooks/user/useCurrentUser';
import useSite from '~/client/hooks/useSite';
import useToastContext from '~/client/layout/DefaultLayout/LayoutContext/Toast/useToastContext';
import useNextLink from '~/client/shared/NextLink/useNextLink';
import useAccountPopoverStore from '~/client/store/useAccountPopoverStore';
import sendInteractionToGtm from '~/shared/gtm/sendInteractionToGtm';
import sendEventToUmami from '~/shared/trackEvent/sendEventToUmami';
import SessionStorage from '~/shared/utils/storage/SessionStorage';
import getOptInRefetchQueries from './getOptInRefetchQueries';
// import SessionStorage from 'app/src/client/utils/storage/SessionStorage';

type Props = {
  tournamentId: string;
  page?: 'list' | 'details' | 'game';
  handle?: string;
};

const useOptin = ({ tournamentId, page, handle }: Props) => {
  const { redirect } = useRedirect();
  const router = useRouter();
  const shortUrl = router?.query?.id?.toString() || '';
  const { user, walletCurrency } = useCurrentUser();
  const { site } = useSite();
  const [optInMutation, { loading }] = useTournamentOptInMutation();
  const { showToast } = useToastContext();
  const { showPopover } = useAccountPopoverStore();
  const redirectPath = router.asPath.replace(/\?.*$/, '');
  const tournamentBannerId = SessionStorage.getItem<string>(
    'tournamentId',
  )?.replace(/"/g, '');
  const isGamePage = router.asPath.includes('/play');
  const { href } = useNextLink({ href: `/login?redirect=${redirectPath}` });

  const handleOptIn = useCallback(async () => {
    sendEventToUmami(
      {
        handle,
      },
      'optIn',
    );

    if (!user?.id) {
      if (page === 'game' || isGamePage) {
        return showPopover('login');
      }
      return redirect(href.toString());
    }

    if (tournamentBannerId === tournamentId) {
      sendInteractionToGtm({
        eventCategory: 'tournament_banner_optIn',
        eventAction: 'optIn_clicked',
        eventInfo: tournamentBannerId,
      });
    }

    const { data } = await optInMutation({
      variables: {
        tournamentId,
        newValue: true,
        currency: walletCurrency,
      },
      refetchQueries: getOptInRefetchQueries({ site, page, shortUrl }),
    });
    if (!data?.casinoTournamentsPublicSetOptInMutation?.errors) {
      return showToast({
        message: 'tournaments.opt-in-successful',
        variant: 'success',
        position: 'bottom',
        timeout: 5000,
      });
    }
    return null;
  }, [
    handle,
    user?.id,
    tournamentBannerId,
    tournamentId,
    optInMutation,
    walletCurrency,
    site,
    page,
    shortUrl,
    isGamePage,
    redirect,
    href,
    showPopover,
    showToast,
  ]);

  return { loading, handleOptIn };
};

export default useOptin;
